'use client';
import { useContext, type ComponentProps } from 'react';
import { SettingsPresentationContext } from './settingsPresentation';
import { Slider } from './Slider';

/** A scalar slider with an aligned, visible reading. Secondary actions belong in SettingsRow.actions. The fixed widths keep
 *  the track ends of stacked rows aligned. `readingWidth="auto"` is for readings of unbounded length (a
 *  number with a manifest-declared unit): the reading takes its own width, wraps under a track that
 *  keeps a usable minimum and breaks its own lines when the row is narrower than the text, instead of
 *  crushing the track or running past the card's edge.
 *
 *  In BOTH modes the reading is one unbreakable value and never shrinks: `whitespace-nowrap` and
 *  `shrink-0`, because a break inside "10k tokenů" reads as two values. The track is the row's only
 *  flexible cell, so a narrow row gives up the track's width instead, which is what keeps a phone-width
 *  row's value on the same line as its slider. In `auto` mode a reading too wide to share the line at all
 *  still moves under the track as a whole on a page. Drawer rows keep even an automatic reading inline
 *  and let the track shrink, so a phone's short value never becomes a separate row. */
export function SliderWithReading({ reading, readingWidth = 'w-20', readingClassName = '', ...props }: ComponentProps<typeof Slider> & {
  reading: string; readingWidth?: 'w-10' | 'w-16' | 'w-20' | 'auto';
  /** Ink for the reading itself, for a row whose value carries a state the track cannot show (a model
   *  limit reading in the quiet ink while it is inherited). The reading's size and alignment stay here. */
  readingClassName?: string;
}) {
  const drawer = useContext(SettingsPresentationContext) === 'drawer';
  const wraps = readingWidth === 'auto' && !drawer;
  return <div className={`flex w-full min-w-0 items-center gap-x-3 ${wraps ? 'flex-wrap gap-y-1' : ''}`}>
    <div className={`${wraps ? 'min-w-24' : 'min-w-0'} flex-1`}><Slider {...props} aria-valuetext={props['aria-valuetext'] ?? reading} /></div>
    <span className={`${wraps ? 'max-w-full' : `${readingWidth === 'auto' ? '' : readingWidth} text-right`} shrink-0 whitespace-nowrap text-ui tabular-nums text-foreground ${readingClassName}`}>{reading}</span>
  </div>;
}
